import { lazy, ReactNode, Suspense, useEffect, useMemo, useRef } from 'react';
import ReactGA from 'react-ga4';
import { useLocation } from 'react-router';

import { Flex, useMediaQuery } from '@chakra-ui/react';
import { useUser } from '@clerk/react';

import { isFloatingAssistantHiddenRoute } from './floatingAssistantRoutes';
import { AlertCard } from '../components/AlertCard';
import { isFloatingAssistantEnabledForEnv } from '../components/assistant/FloatingAssistant/drawerConstants';
import { GlobalConnectEmailPromptDialog } from '../components/GlobalConnectEmailPromptDialog';
import { GlobalDoBoxFormModal } from '../components/GlobalDoBoxFormModal';
import { GlobalMessageDialog } from '../components/MessageDialog';
import { Sidebar } from '../components/Sidebar';
import { useTerminalConnection } from '../components/Terminal/hooks/useTerminalConnection';
import { useTerminalShortcut } from '../components/Terminal/hooks/useTerminalShortcut';
import { TrialBannerContainer } from '../components/TrialBanner';
import { MOBILE_VIEWPORT_QUERY } from '../enums/screenBreakpoints';
import { useTrialGate } from '../hooks/Billing/useTrialGate';
import { useFetchSidebarCounters } from '../hooks/DataFetch';
import { useGTMActiveTeam } from '../hooks/GTM';
import { useOnInit } from '../hooks/OnInit/useOnInit';
import { useActiveTeamClerkSync } from '../hooks/Team';
import { useAlertQueue } from '../hooks/useAlertQueue';
import { useGA4Initialization } from '../hooks/useGA4Initialization';
import { useIntercomInitialization } from '../hooks/useIntercomInitialization';
import { APP_PAGES } from '../routes/constants';
import { useTerminalStore } from '../store/Terminal/useTerminalStore';

const FloatingAssistantContainer = lazy(
  () =>
    import(
      '../pages/private/Assistant/components/FloatingAssistantContainer/FloatingAssistantContainer'
    ),
);

const TerminalDock = lazy(
  () => import('../components/Terminal/components/TerminalDock/TerminalDock'),
);

interface LayoutProps {
  children: ReactNode;
}

const stylesheet = {
  main: 'relative w-full small-height:min-h-[650px] h-dvh overflow-hidden m-auto bg-semantic-bg-page flex flex-col',
  container: 'w-full flex-1 min-h-0 overflow-auto relative',
  topScrollDiv: 'w-full h-[30px] md:h-[60px] fixed top-0 bg-transparent',
  bottomScrollDiv: 'w-full h-[30px] h-[60px] fixed bottom-0 bg-transparent',
  scrollDivActive: 'z-[1200]',
  scrollDivInactive: '-z-10',
  contentColumn: 'relative flex-1 min-w-0 h-full flex flex-col',
  mainContent:
    'flex-1 min-h-0 w-full overflow-auto relative md:isolate scrollbar-hide',
  mainContentHidden: 'hidden',
};

const sidebarRoutesHidden = ['/meeting/run', APP_PAGES.BRAIN];
const intercomRoutesVisible = [APP_PAGES.SUPPORT];

export function Layout({ children }: Readonly<LayoutProps>) {
  const { user } = useUser();
  const { currentAlert } = useAlertQueue();

  const containerRef = useRef<HTMLDivElement | null>(null);
  const location = useLocation();

  const [isMobile] = useMediaQuery(MOBILE_VIEWPORT_QUERY);

  const isMobileAndInboxPreviewRoute =
    isMobile && location.pathname.includes('inbox/preview/');

  useGA4Initialization();
  useIntercomInitialization();
  useGTMActiveTeam();
  useActiveTeamClerkSync();
  useTrialGate();
  useTerminalConnection();
  useTerminalShortcut();
  const hasOpenedTerminal = useTerminalStore((state) => state.hasOpened);
  const isTerminalMaximized = useTerminalStore(
    (state) => state.isOpen && state.isMaximized,
  );

  useEffect(() => {
    if (user) {
      ReactGA.set({ user_id: user.id });

      ReactGA.set({
        user_properties: {
          id: user.id,
          email: user.emailAddresses[0]?.emailAddress,
          firstName: user.firstName,
          lastName: user.lastName,
        },
      });
    } else {
      ReactGA.set({ user_id: null });
    }
  }, [user]);

  const hideSidebar = sidebarRoutesHidden.some((route) =>
    location.pathname.includes(route),
  );

  const shouldShowFloatingAssistant =
    isFloatingAssistantEnabledForEnv &&
    !isFloatingAssistantHiddenRoute(location.pathname);

  const shouldShowSidebar = useMemo(() => {
    if (hideSidebar) {
      return false;
    }
    if (isMobileAndInboxPreviewRoute) {
      return false;
    }
    return true;
  }, [hideSidebar, isMobileAndInboxPreviewRoute]);

  useEffect(() => {
    const shouldShowIntercom = intercomRoutesVisible.some((route) =>
      location.pathname.startsWith(route),
    );

    if (window.Intercom) {
      window.Intercom('update', { hide_default_launcher: !shouldShowIntercom });
    }
  }, [location.pathname]);

  useOnInit();
  useFetchSidebarCounters(shouldShowSidebar);

  return (
    <>
      <main className={stylesheet.main}>
        {currentAlert && <AlertCard {...currentAlert} />}

        <TrialBannerContainer />

        <Flex className={stylesheet.container}>
          {shouldShowSidebar && <Sidebar />}
          <div className={stylesheet.contentColumn}>
            <div
              id="table-container"
              ref={containerRef}
              className={`${stylesheet.mainContent} ${isTerminalMaximized ? stylesheet.mainContentHidden : ''}`}
              data-testid="app-main-content"
            >
              {children}
            </div>
            {hasOpenedTerminal && (
              <Suspense fallback={null}>
                <TerminalDock />
              </Suspense>
            )}
          </div>
        </Flex>
      </main>
      <GlobalMessageDialog />
      <GlobalDoBoxFormModal />
      <GlobalConnectEmailPromptDialog />
      {shouldShowFloatingAssistant && (
        <Suspense fallback={null}>
          <FloatingAssistantContainer />
        </Suspense>
      )}
    </>
  );
}
